/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import { IPaymentInvoiceModel, TimeHelper } from "@nf/common";
import { useTranslation } from "next-i18next";

import ActionsHandler from "components/ActionsHandler";
import { CommonTextError } from "components/CommonTextError";
import { useCurrentSubscription } from "hooks/useCurrentSubscription";
import { useDownloadInvoicePDF } from "./hooks/useDownloadInvoicePDF";
import { usePaymentInvoices } from "./hooks/usePaymentInvoices";
import { PaymentDetailType } from "components/PaymentDetailType";
import { ITableColumn, Table } from "components/Table";

import styles from "./SettingsPaymentHistory.module.scss";

export const SettingsPaymentHistory = () => {
  const { t } = useTranslation("translations");
  const subscription = useCurrentSubscription();
  const {
    invoices,
    error: invoicesError,
    isLoading: invoicesLoading,
  } = usePaymentInvoices();
  const {
    download: downloadInvoicePDF,
    error: invoicePDFError,
    isLoading: invoicePDFLoading,
  } = useDownloadInvoicePDF();

  const columns: ITableColumn<IPaymentInvoiceModel>[] = [
    {
      title: t("web_payment_history_header_date"),
      key: "date",
      render: (invoice) => (
        <button
          type="button"
          className={styles.DownloadButton}
          disabled={invoicePDFLoading}
          onClick={() => downloadInvoicePDF(invoice)}
        >
          {TimeHelper.format(invoice.IssuedAt, "DD/MM-YY")}
        </button>
      ),
    },
    {
      title: t("web_payment_history_header_subscription"),
      key: "subscription",
      render: (invoice) => invoice.SubscriptionName,
    },
    {
      title: t("web_payment_history_header_amount"),
      key: "amount",
      render: ({ AmountIncludingVat, Currency }) =>
        `${AmountIncludingVat.toFixed(
          Number.isInteger(AmountIncludingVat) ? 0 : 2
        )} ${Currency}`,
    },
  ];

  return (
    <div className={styles.Container}>
      {subscription ? (
        <>
          <div className={styles.Item}>
            <PaymentDetailType subscription={subscription} />
          </div>
          <div className={styles.Item}>
            <span className={styles.ItemTitle}>
              {subscription.IsActive
                ? t("web_subscription_next_renewal")
                : t("web_subscription_subscription_ends")}
              :
            </span>
            {subscription.ExpirationDateLabel}
          </div>
        </>
      ) : (
        <div className={styles.Item}>
          <span>{t("web_subscription_no_subscription")}</span>
        </div>
      )}

      <div className={styles.Item}>
        <p>{t("web_payment_history_description")}</p>
      </div>

      {invoicePDFError && (
        <div className={styles.Item}>
          <CommonTextError error={invoicePDFError} />
        </div>
      )}

      <div className={styles.Item}>
        {invoicesError ? (
          <CommonTextError error={invoicesError} />
        ) : (
          <ActionsHandler isLoading={invoicesLoading}>
            <Table
              className={styles.Table}
              columns={columns}
              data={invoices}
              rowKey="Id"
              emptyText={t("web_my_account_menu_devices_no_data_text")}
            />
          </ActionsHandler>
        )}
      </div>
    </div>
  );
};

export default SettingsPaymentHistory;
